import { FC, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { CompanyLogo, Select } from '@pepper/ui'

import s from './GoogleAccountPropertySelector.module.css'

type Props = {
  title: string
  name: 'ga' | 'gsc'
  options: {
    label: {
      header: string
      subHeader?: string
    }
    value: string
  }[]
  isValid?: boolean
  value?: string
  disabled?: boolean
  allowClear?: boolean
  onChange: (value?: string) => void
}

export const GoogleAccountPropertySelector: FC<Props> = ({
  title,
  onChange,
  options,
  name,
  isValid,
  value,
  disabled = false,
  allowClear = true,
}) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value)

  const placeholder = useMemo(() => {
    if (options.length > 0) {
      if (name === 'ga') {
        return 'Select GA property'
      }
      return 'Select GSC property'
    }
    return 'No Access'
  }, [options])

  const handleChange = (currentValue?: string) => {
    setSelectedValue(currentValue)
    onChange(currentValue)
  }

  useEffect(() => {
    if (value) {
      handleChange(value)
    } else {
      onChange(undefined)
    }
  }, [value])

  return (
    <div className={s.wrapper}>
      <div className={s.label}>
        {title}: <span className={s.property}>Property</span>
      </div>
      <Select
        disabled={disabled || options.length === 0}
        placeholder={placeholder}
        allowClear={allowClear}
        onClear={() => setSelectedValue(undefined)}
        onChange={handleChange}
        value={selectedValue}
        options={options.map(option => ({
          label: (
            <div className={s.option}>
              <CompanyLogo
                companyName={option.label.header}
                className={s.logo}
              />
              {option.label.header}
              {option.label.subHeader && (
                <span className={s.subHeader}>{option.label.subHeader}</span>
              )}
            </div>
          ),
          value: option.value,
        }))}
      />
      {!!selectedValue && !isValid && (
        <div className={s.helper}>
          <Icon icon="lucide:alert-triangle" className={s.icon} width={16} />
          There is not enough data in this property for your domain.
        </div>
      )}
    </div>
  )
}
